export type EnumMaps = Record<string, string[]>

/** Agent-facing enums: a flat list, or synonym groups keyed by canonical value. */
export type EnumJson = Record<string, string[] | Record<string, string[]>>

export interface EnumIndex {
  values: EnumMaps
  /** enumName → UPPER(alias) → canonical value used when joining. */
  aliases: Record<string, Record<string, string>>
}

/**
 * Flatten agent enums into regex surface forms plus a join-time alias index.
 * `EquipmentType: { VALVE: ["V", "VALVE"] }` makes A `V` join B `VALVE`.
 */
export function indexEnumJson(input: unknown): EnumIndex {
  if (input == null) return { values: {}, aliases: {} }
  if (typeof input === 'string') return indexEnumJson(parseEnumMaps(input))
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    throw new Error('enums must be an object of name → string[] or name → { canonical: string[] }')
  }
  const values: EnumMaps = {}
  const aliases: Record<string, Record<string, string>> = {}
  for (const [name, raw] of Object.entries(input as Record<string, unknown>)) {
    const indexed = indexOneEnum(name, raw)
    values[name] = indexed.values
    aliases[name] = indexed.aliases
  }
  return { values, aliases }
}

function indexOneEnum(
  name: string,
  raw: unknown
): { values: string[]; aliases: Record<string, string> } {
  if (Array.isArray(raw)) {
    if (raw.some((entry) => typeof entry !== 'string')) {
      throw new Error(`enums.${name} must be an array of strings or an object of synonym groups`)
    }
    const list = uniquePreserveOrder(raw.map((entry) => entry.trim()).filter(Boolean))
    if (list.length === 0) throw new Error(`Enum map ${name} has no values`)
    return { values: list, aliases: identityAliases(list) }
  }
  if (!raw || typeof raw !== 'object') {
    throw new Error(`enums.${name} must be an array of strings or an object of synonym groups`)
  }
  const aliases: Record<string, string> = {}
  const values: string[] = []
  for (const [canonicalRaw, group] of Object.entries(raw as Record<string, unknown>)) {
    const canonical = canonicalRaw.trim()
    if (!canonical) continue
    if (!Array.isArray(group) || group.some((entry) => typeof entry !== 'string')) {
      throw new Error(`enums.${name}.${canonicalRaw} must be an array of strings`)
    }
    const groupValues = uniquePreserveOrder(
      [canonical, ...group.map((entry) => entry.trim())].filter(Boolean)
    )
    if (groupValues.length === 0) continue
    for (const alias of groupValues) {
      const key = alias.toUpperCase()
      const existing = aliases[key]
      if (existing && existing.toUpperCase() !== canonical.toUpperCase()) {
        throw new Error(`enums.${name}: alias ${alias} maps to both ${existing} and ${canonical}`)
      }
      aliases[key] = canonical
      if (!values.some((value) => value.toUpperCase() === key)) values.push(alias)
    }
  }
  if (values.length === 0) throw new Error(`Enum map ${name} has no values`)
  return { values, aliases }
}

function identityAliases(values: string[]): Record<string, string> {
  const aliases: Record<string, string> = {}
  for (const value of values) aliases[value.toUpperCase()] = value
  return aliases
}

/**
 * Parse operator-authored enum maps.
 *
 * Accepted forms:
 * - JSON object `{ "InstrumentRole": ["PIT", "PDT"] }`
 * - Lines `InstrumentRole: PIT, PDT, TT`
 * - A header line `EnumMaps: InstrumentRole` is ignored (declaration only)
 */
export function parseEnumMaps(raw: string): EnumMaps {
  const trimmed = raw.trim()
  if (!trimmed) return {}
  if (trimmed.startsWith('{')) {
    return parseJsonEnumMaps(trimmed)
  }
  const maps: EnumMaps = {}
  for (const line of trimmed.split(/\r?\n/)) {
    const text = line.trim()
    if (!text || text.startsWith('#')) continue
    const match = /^([A-Za-z_][A-Za-z0-9_]*)\s*[:=]\s*(.*)$/.exec(text)
    if (!match) {
      throw new Error(`Invalid enum map line: ${text}`)
    }
    const name = match[1]
    const rest = match[2].trim()
    if (name === 'EnumMaps') continue
    const values = rest
      .split(/[,;]/)
      .map((part) => part.trim())
      .filter((part) => part.length > 0)
    if (values.length === 0) {
      throw new Error(`Enum map ${name} has no values`)
    }
    maps[name] = uniquePreserveOrder(values)
  }
  return maps
}

function parseJsonEnumMaps(raw: string): EnumMaps {
  let parsed: unknown
  try {
    parsed = JSON.parse(raw) as unknown
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    throw new Error(`Invalid enum map JSON: ${message}`)
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Enum map JSON must be an object of name → string[]')
  }
  const maps: EnumMaps = {}
  for (const [name, value] of Object.entries(parsed as Record<string, unknown>)) {
    if (!Array.isArray(value) || value.some((entry) => typeof entry !== 'string')) {
      throw new Error(`Enum map ${name} must be an array of strings`)
    }
    const values = uniquePreserveOrder(value.map((entry) => entry.trim()).filter(Boolean))
    if (values.length === 0) throw new Error(`Enum map ${name} has no values`)
    maps[name] = values
  }
  return maps
}

function uniquePreserveOrder(values: string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const value of values) {
    const key = value.toUpperCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push(value)
  }
  return out
}
